/**
 * ItemSection.jsx — 홍보물 원가 관리 섹션
 *
 * 기능:
 *  - 홍보물 목록 테이블 (홍보물명·발주량·판매가·원가·발주처·기본택배사·상태·송장불필요)
 *  - 행별 이력 accordion (판매가·원가 변경 이력)
 *  - 홍보물 수정 모달 (Modal 컴포넌트 사용)
 *
 * Named export: ItemSection
 */
import React from 'react';
import Modal from '../../../components/Modal';
import { toKoreanDateTimeString } from '../../../utils/date';
import {
  SortIcon,
  cardStyle, cardHeaderStyle, cardTitleStyle,
  fieldGroup, fieldLabel, inputStyle,
  tableStyle, thStyle, tdStyle, trStyle,
  addBtnStyle, saveBtnStyle, cancelBtnStyle, editBtnStyle, historyBtnStyle,
} from './styles';
import { stdBadgeActive, stdBadgeMuted } from '../adminlayout/designStandard';

// ── 포맷 헬퍼 ─────────────────────────────────────────────────────────────────
// 금액 포맷 (천단위 콤마 + '원' 접미사)
const fmtPrice = (n) => (n != null ? Number(n).toLocaleString() + '원' : '-');

// KST 날짜+시간 포맷 (이력 변경일시 표시용)
function fmtDateTime(ts) {
  // toKoreanDateTimeString: utils/date.js 사용 (new Date 직접 사용 금지)
  return toKoreanDateTimeString(ts);
}

/**
 * 홍보물 원가 관리 카드 섹션
 *
 * Props:
 *   sortedItems       {Array}       — 정렬된 홍보물 목록
 *   itemSort          {object}      — { key, dir } 현재 정렬 상태
 *   onSortChange      {function}    — (key) 정렬 키 변경 핸들러
 *   historyMap        {object}      — itemId → history 배열 (null: 미로드)
 *   openHistoryId     {number|null} — 현재 열린 이력 accordion의 item id
 *   onToggleHistory   {function}    — (itemId) 이력 accordion 토글 핸들러
 *   editingItem       {object|null} — 편집 중인 홍보물 객체
 *   setEditingItem    {function}    — editingItem 상태 변경
 *   activeSuppliers   {Array}       — 활성 발주처 목록 (선택 드롭다운용)
 *   previewCourier    {string}      — 선택된 발주처의 기본택배사 미리보기
 *   onSupplierChange  {function}    — (supplierId) 발주처 변경 핸들러
 *   saving            {boolean}     — 저장/삭제 진행 중 여부
 *   onSave            {function}    — 저장 버튼 클릭 핸들러
 *   emptyItem         {function}    — 빈 홍보물 객체 생성 함수
 */
export function ItemSection({
  sortedItems,
  itemSort,
  onSortChange,
  historyMap,
  openHistoryId,
  onToggleHistory,
  editingItem,
  setEditingItem,
  activeSuppliers,
  previewCourier,
  onSupplierChange,
  saving,
  onSave,
  emptyItem,
}) {
  return (
    <>
      {/* 홍보물 원가 관리 카드 */}
      <div style={cardStyle}>
        <div style={cardHeaderStyle}>
          <span style={cardTitleStyle}>홍보물 원가 관리</span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              홍보물 관리 &gt; 홍보물 금액 관리와 동일 데이터
            </span>
            <button style={addBtnStyle} onClick={() => setEditingItem(emptyItem())}>
              + 홍보물 추가
            </button>
          </div>
        </div>

        {/* 홍보물 목록 테이블 */}
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                {/* 정렬 가능한 헤더 열 */}
                {[
                  { label: '홍보물명',    key: 'name' },
                  { label: '발주량',      key: 'total_order_qty' },
                  { label: '판매가',      key: 'price' },
                  { label: '원가',        key: 'cost_price' },
                  { label: '발주처',      key: 'supplier_name' },
                  { label: '기본택배사',  key: 'supplier_default_courier' },
                  { label: '상태',        key: 'is_active' },
                  { label: '송장불필요',  key: 'no_shipping' },
                ].map(({ label, key }) => (
                  <th
                    key={key}
                    style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => onSortChange(key)}
                  >
                    {label}
                    <SortIcon current={itemSort} colKey={key} />
                  </th>
                ))}
                {/* 이력·수정 버튼 열 (정렬 없음) */}
                <th style={thStyle}>이력</th>
                <th style={thStyle} />
              </tr>
            </thead>
            <tbody>
              {/* 목록 없을 때 안내 */}
              {sortedItems.length === 0 && (
                <tr>
                  <td colSpan={10} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-muted)' }}>
                    홍보물이 없습니다.
                  </td>
                </tr>
              )}

              {sortedItems.map((item, idx) => {
                // 마지막 행(이력 accordion 닫힘)은 밑줄 제거 — 카드 테두리와 겹침 방지 (디자인 표준)
                const isLastRow = idx === sortedItems.length - 1 && openHistoryId !== item.id;
                const rowTd = isLastRow ? { ...tdStyle, borderBottom: 'none' } : tdStyle;
                return (
                <React.Fragment key={item.id}>
                  {/* 데이터 행 */}
                  <tr style={{ ...trStyle, opacity: item.is_active ? 1 : 0.5 }}>
                    <td style={{ ...rowTd, fontWeight: 700 }}>{item.name}</td>

                    {/* 총 발주량 */}
                    <td style={{ ...rowTd, textAlign: 'right', fontWeight: 700, color: item.total_order_qty > 0 ? 'var(--text-sub)' : 'var(--border)' }}>
                      {item.total_order_qty > 0 ? item.total_order_qty.toLocaleString() : '-'}
                    </td>

                    <td style={{ ...rowTd, fontWeight: 700, color: '#1e40af' }}>{fmtPrice(item.price)}</td>
                    <td style={{ ...rowTd, fontWeight: 700, color: '#7c3aed' }}>{fmtPrice(item.cost_price)}</td>
                    <td style={rowTd}>{item.supplier_name || <span style={{ color: 'var(--border)' }}>-</span>}</td>
                    <td style={rowTd}>{item.supplier_default_courier || <span style={{ color: 'var(--border)' }}>-</span>}</td>

                    {/* 활성/비활성 배지 — 디자인 표준 배지 */}
                    <td style={rowTd}>
                      <span style={item.is_active ? stdBadgeActive : stdBadgeMuted}>
                        {item.is_active ? '활성' : '비활성'}
                      </span>
                    </td>

                    {/* 송장 불필요 여부 배지 */}
                    <td style={{ ...rowTd, textAlign: 'center' }}>
                      {item.no_shipping
                        ? <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: '#fef3c7', color: '#92400e' }}>불필요</span>
                        : <span style={{ color: 'var(--border)', fontSize: 12 }}>-</span>
                      }
                    </td>

                    {/* 이력 accordion 토글 버튼 */}
                    <td style={rowTd}>
                      <button
                        style={{ ...historyBtnStyle, background: openHistoryId === item.id ? 'var(--bg-subtle)' : 'var(--bg-card)' }}
                        onClick={() => onToggleHistory(item.id)}
                      >
                        {openHistoryId === item.id ? '▲ 닫기' : '▼ 이력'}
                      </button>
                    </td>

                    {/* 수정 버튼 */}
                    <td style={{ ...rowTd, whiteSpace: 'nowrap' }}>
                      <button
                        style={editBtnStyle}
                        onClick={() => setEditingItem({
                          id:          item.id,
                          name:        item.name,
                          price:       String(item.price),
                          cost_price:  String(item.cost_price),
                          supplier_id: item.supplier_id || '',
                          is_active:   item.is_active,
                          no_shipping: Boolean(item.no_shipping),
                        })}
                      >
                        수정
                      </button>
                    </td>
                  </tr>

                  {/* 이력 accordion (열린 경우만 렌더) — 색 배경/테두리 → 중립 톤 (디자인 표준) */}
                  {openHistoryId === item.id && (
                    <tr>
                      <td colSpan={10} style={{ padding: 0, background: 'var(--bg-subtle)' }}>
                        <div style={{ padding: '10px 20px 14px' }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-sub)', marginBottom: 8 }}>
                            판매가·원가 변경 이력
                          </div>
                          {!historyMap[item.id] ? (
                            <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>로딩 중…</div>
                          ) : historyMap[item.id].length === 0 ? (
                            <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>이력이 없습니다.</div>
                          ) : (
                            <table style={{ ...tableStyle, margin: 0, background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                              <thead>
                                <tr>
                                  {['변경일시', '판매가', '원가', '변경자'].map((h) => (
                                    <th key={h} style={thStyle}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {historyMap[item.id].map((h, k) => (
                                  <tr key={h.id} style={{ background: k === 0 ? 'var(--bg-subtle)' : 'var(--bg-card)' }}>
                                    <td style={{ ...tdStyle, fontSize: 12 }}>{fmtDateTime(h.changed_at)}</td>
                                    <td style={{ ...tdStyle, fontSize: 12, fontWeight: 700, color: '#1e40af' }}>{fmtPrice(h.price)}</td>
                                    <td style={{ ...tdStyle, fontSize: 12, fontWeight: 700, color: '#7c3aed' }}>{fmtPrice(h.cost_price)}</td>
                                    <td style={{ ...tdStyle, fontSize: 12 }}>{h.changed_by_name || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── 홍보물 수정/추가 모달 ── */}
      <Modal
        open={!!editingItem}
        title={editingItem?.id ? '홍보물 수정' : '홍보물 추가'}
        icon="📦"
        onClose={() => setEditingItem(null)}
        width="min(500px, 92vw)"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button style={cancelBtnStyle} onClick={() => setEditingItem(null)}>취소</button>
            <button style={saveBtnStyle} disabled={saving} onClick={onSave}>
              {saving ? '저장중…' : '저장'}
            </button>
          </div>
        }
      >
        {editingItem && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* 홍보물명 입력 */}
            <div style={fieldGroup}>
              <label style={fieldLabel}>홍보물명 *</label>
              <input
                style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                placeholder="홍보물명"
                value={editingItem.name}
                autoFocus
                onChange={(e) => setEditingItem((p) => ({ ...p, name: e.target.value }))}
              />
            </div>

            {/* 판매가 / 원가 나란히 */}
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ ...fieldGroup, flex: 1 }}>
                <label style={fieldLabel}>판매가 (원)</label>
                <input
                  style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                  type="number" min="0" placeholder="0"
                  value={editingItem.price}
                  onChange={(e) => setEditingItem((p) => ({ ...p, price: e.target.value }))}
                />
              </div>
              <div style={{ ...fieldGroup, flex: 1 }}>
                <label style={fieldLabel}>원가 (원)</label>
                <input
                  style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                  type="number" min="0" placeholder="0"
                  value={editingItem.cost_price}
                  onChange={(e) => setEditingItem((p) => ({ ...p, cost_price: e.target.value }))}
                />
              </div>
            </div>

            {/* 발주처 드롭다운 (기본택배사 미리보기 포함) */}
            <div style={fieldGroup}>
              <label style={fieldLabel}>
                발주처
                {previewCourier && (
                  <span style={{ marginLeft: 6, color: '#7c3aed', fontWeight: 400 }}>
                    → 기본택배: {previewCourier}
                  </span>
                )}
              </label>
              <select
                style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                value={editingItem.supplier_id || ''}
                onChange={(e) => onSupplierChange(e.target.value)}
              >
                <option value="">발주처 선택</option>
                {activeSuppliers.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>

            {/* 활성 여부 + 송장 불필요 */}
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ ...fieldGroup, width: 120 }}>
                <label style={fieldLabel}>상태</label>
                <select
                  style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                  value={editingItem.is_active ? 'true' : 'false'}
                  onChange={(e) => setEditingItem((p) => ({ ...p, is_active: e.target.value === 'true' }))}
                >
                  <option value="true">활성</option>
                  <option value="false">비활성</option>
                </select>
              </div>
              {/* 송장 불필요: 블로그 체험단·인스타 시안 등 발송 없는 항목 */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-sub)', cursor: 'pointer', paddingBottom: 6 }}>
                <input
                  type="checkbox"
                  checked={Boolean(editingItem.no_shipping)}
                  onChange={(e) => setEditingItem((p) => ({ ...p, no_shipping: e.target.checked }))}
                  style={{ width: 15, height: 15, accentColor: '#7c3aed', cursor: 'pointer' }}
                />
                <span>송장 불필요</span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>(송장 없이 완료 처리)</span>
              </label>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
